import { DatePipe } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, combineLatest, filter, Observable, of, Subject, takeUntil, withLatestFrom } from 'rxjs';
import {
  selectHasGoingDigitalFlowInternalTransferCreateSloa,
  selectHasGoingDigitalFlowWireCreateSloa,
  selectInfo,
  selectIsDemoUser,
  selectIsEmployee,
  selectIsEstateOrTestTrust,
  selectIsIra,
  selectIsOrgAccount,
  selectIsUserInputRequired,
  selectSubAccountId,
  selectUsername,
  selectUseTestTrustDigitalToPdfFlow,
} from '../../../account-info-header/reducers';
import { selectHasAccessInitiateMoneyMovement, selectHasAccessReleaseMoneyMovement } from '../../../authorize/reducers';
import { ReviewPageActions } from '../../../create-request/actions';
import { mapInitiatedDraftsToTableColumn } from '../../../create-request/components/transactions-table/table-helpers';
import { Acknowledgement } from '../../../create-request/models/review-page/ReviewPage';
import { Revalidation } from '../../../create-request/models/shared/Revalidation';
import { WarningOrError } from '../../../create-request/models/shared/WarningOrError';
import { selectAllEauthAccountHoldersHaveEmailAddress, selectAllSelectedEauthAccountHoldersHaveEmailAddress } from '../../../create-request/reducers';
import { InitReleaseActions } from '../../../initiate-and-release/actions';
import { DraftListTableColumn } from '../../../initiate-and-release/Models/Drafts/DraftResponse';
import { selectInitReleaseTransactionInContext } from '../../../initiate-and-release/reducers';
import { EAuthActions } from '../../../shared/actions';
import { displayCancelModal, resetCancelModalforInitiateAndReleaseTable } from '../../../shared/actions/shared.actions';
import { EAuthInfo } from '../../../shared/models/EAuthInfo';
import {
  selectAcknowledgement,
  selectAcknowledgementError,
  selectCancelModalContinueClicked,
  selectCancelModalSource,
  selectEAuthInfo,
  selectPageScrollTo,
  selectPdf,
  selectReviewPage,
  selectReviewPageSubmitClicked,
} from '../../../shared/reducers';
import { ComponentState } from '../../../shared/types/ComponentState';
import {
  ApiEndpointType,
  Constants,
  distributionReasons,
  moneyMovementAcknowledgeError,
  navRouteConstants,
  prettierWarnings,
  RegTypeCode,
  stateCodes,
  stateTypeIndicators,
  transactionTypes,
} from '../../../shared/utilities/Constants';
import { getArrayBufferFromBase64, openPdfInNewWindow } from '../../../shared/utilities/pdf';
import {
  StandingAuthAchActions,
  StandingAuthCheckActions,
  StandingAuthDetailsActions,
  StandingAuthInternalTransferActions,
  StandingAuthReviewPageActions,
  StandingAuthWireActions,
} from '../../actions';
import { InternalTransferInfoSA } from '../../models/InternalTransferInfoSA';
import { CheckStandingAuthDetails, StandingAuthFormServiceError, StandingAuthorizationForm, WireStandingAuthDetails } from '../../models/standingAuthorizationForm';
import {
  isCheckStandingAuthDetails,
  selectAchAcknowledgeCallError,
  selectAchProcessDate,
  selectAchRecurringFrequency,
  selectAchVerifyCallWarning,
  selectAllEauthClientsAwaitingAlliance,
  selectCheckPendingForCTWE,
  selectClientAuthorizationNeeded,
  selectEAuthModel,
  selectInternalTransferPendingForCTWE,
  selectIsFromTableOrView,
  selectIsSubmitandCreateBundledSa,
  selectRevalidationResponse,
  selectSaPendReasons,
  selectShowClientAuthorization,
  selectStandingAuthCanRelease,
  selectStandingAuthForm,
  selectStandingAuthFormServiceError,
  selectStandingAuthInternalTransferInfo,
  selectStandingAuthPendForRelease,
  selectStandingAuthPendForReleaseCustomRule,
  selectTransactionType,
  selectSLOAVerifyCallFinished,
  selectWarningList,
} from '../../reducers';
import { positionPage } from '../../../shared/utilities/PrintPageUtils';
import { DateTime } from 'luxon';
import { Hyperlink } from '../../../shared/types/Hyperlink';
import { Router } from '@angular/router';

@Component({
  standalone: false,
  selector: 'mm-standing-authorization-review-page',
  templateUrl: './sa-review-page.component.html',
  styleUrls: ['./sa-review-page.component.scss'],
})
export class SaReviewPageComponent implements OnInit, OnDestroy {
  reviewPageCopy: string = Constants.reviewPageSACopy;
  showClientAuthorization: boolean;
  eAuthRestricted: boolean;
  eAuthIneligible: boolean;
  hasMmInitiateAccess: boolean | undefined;
  acknowledgement: Acknowledgement;
  errors$: Observable<WarningOrError | undefined>;
  eAuthInfo: EAuthInfo;
  form: StandingAuthorizationForm;
  reviewPageComponentState$: Observable<ComponentState>;
  submitClicked$: Observable<boolean>;
  formServiceError$: Observable<StandingAuthFormServiceError>;
  errorSummaryText: string = Constants.moneyMovementAcknowledgeErrorSummary;
  attentionMessageRequired: boolean;
  warningList: WarningOrError[];
  warningMessages: Set<string> = new Set<string>();
  userInputRequired: boolean;
  PendingReasonsList: string[] = [];
  clientAuthorizationNeeded: boolean;
  isEmployee: boolean;
  isDemoUser: boolean;
  destroy$ = new Subject<void>();
  transactionType: string | undefined;
  checkPendingForCTWE: boolean;
  internalTransferPendingForCTWE: boolean;
  itInfo: InternalTransferInfoSA;
  stateCode: string = '';
  stateTypeIndicator: string = '';
  defaultStateTax: string;
  reviewStateTax: string;
  hasATSSystemError: boolean;
  atsSystemErrorText: string;
  hasServiceError: boolean = false;
  hasBadRequestServiceError: boolean = false;
  serviceErrorList: string[];
  allEauthAccountHoldersHaveAnEmailAddress: boolean;
  isOrgAccount: boolean;
  useTestTrustDigitalToPdfFlow: boolean;
  isEstateOrTestTrust: boolean;
  isQcdDistributionReason: boolean;
  showPositionsTable: boolean = true;
  isPendForRelease: boolean = false;
  isSubmitAndCreateBundledSa: boolean = false;
  isCheckSloa: boolean;
  isQCDCheckSloa: boolean;
  isInitiator: boolean;
  isReleasor: boolean;
  revalidationResponse: Revalidation;
  canRelease: boolean;
  isSloa: boolean;
  deleteDraftClicked$ = new BehaviorSubject<boolean>(false);
  currentDraftInContext$ = new BehaviorSubject<DraftListTableColumn | null>(null);
  hasServiceErrorFORINRTable: boolean;
  singleRevalidationError: string;
  multipleRevalidationError: string[] = [];
  isLivingTrustSaFlow: boolean = false;
  isISOCompliant: boolean;
  nonIsoCompliantSloaInRError: string = Constants.invalidStandingAuthorizationInitiateAndRelease;
   hasAtpTimeoutError: boolean = false;
  shouldButtonsBeDisabled: boolean = false;
  isErrorSubmit: boolean;
  atpErrorOnSubmitToggleOnBannerTitleText: string = '';
  atpErrorOnSubmitToggleOnBannerMessage: string = '';
  errorLink: Hyperlink = {
    url: '',
    text: 'Move Money',
    target: '_self',
    isActionCenterHyperLink: false
  }
  userName: string = '';
  isI401kToAccount: boolean = false;
  isNotInitReleaseFlowCustomRules: boolean = false;
  achWarningMessageList: WarningOrError[]

  constructor(private store: Store, private toastr: ToastrService, private datePipe: DatePipe, private router: Router) { }

  ngOnInit(): void {
    this.store
      .select(selectTransactionType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => (this.transactionType = val));

    this.store
      .select(selectPageScrollTo)
      .pipe(
        takeUntil(this.destroy$),
        filter((scrollTo) => scrollTo === 'sa-review-page-header')
      )
      .subscribe((_) => {
        this.scrollToTop();
      });

    this.store.select(selectAchRecurringFrequency).pipe(takeUntil(this.destroy$), withLatestFrom(this.store.select(selectAchProcessDate))).subscribe(([frequency, processDate]) => {
      if (frequency === 'LASTBIZ') {
        this.store.dispatch(StandingAuthDetailsActions.loadWithdrawalDate({ completionDate: this.datePipe.transform(processDate, 'yyyy-MM-dd') ?? '' }));
      }
    });

    this.store
      .select(selectEAuthModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe((eAuthModel) => {
        this.eAuthRestricted =
          eAuthModel.accountRestricted ||
          (!eAuthModel.eauthInd && !eAuthModel.isTransactionFromSLOAOrSLOA) ||
          (eAuthModel.eauthInd && eAuthModel.allAccountHolderIneligible);
        this.eAuthIneligible = eAuthModel.allAccountHolderIneligible;
      });

    this.store
      .select(selectShowClientAuthorization)
      .pipe(takeUntil(this.destroy$))
      .subscribe((show) => {
        this.showClientAuthorization = show;
      });

    combineLatest([
      this.store.select(selectUseTestTrustDigitalToPdfFlow),
      this.store.select(selectHasGoingDigitalFlowInternalTransferCreateSloa),
      this.store.select(selectTransactionType)
    ])
      .pipe(takeUntil(this.destroy$)).subscribe(([useTestTrustDigitalToPdfFlow, hasGoingDigitalFlowInternalTransferCreateSloa, transactionType]) => {
        if (transactionType === transactionTypes.internalTransfer) {
          this.useTestTrustDigitalToPdfFlow = useTestTrustDigitalToPdfFlow && !hasGoingDigitalFlowInternalTransferCreateSloa;
        }
      });


    this.store.select(selectStandingAuthPendForReleaseCustomRule).pipe(takeUntil(this.destroy$)).subscribe(noRuleReturned => {
      this.isNotInitReleaseFlowCustomRules = noRuleReturned;
    });

    this.store
      .select(selectClientAuthorizationNeeded)
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => (this.clientAuthorizationNeeded = val));

    this.store
      .select(selectCancelModalContinueClicked)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.store.select(selectCancelModalSource)),
        filter(
          ([continueClicked, source]) =>
            (source === 'standingAuthReviewPage' || source === 'editTweModalReviewPage') && continueClicked
        )
      )
      .subscribe((_) => {
        this.store.dispatch(StandingAuthReviewPageActions.cancelClicked());
      });

    this.store
      .select(selectHasAccessInitiateMoneyMovement)
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasAccess) => {
        this.hasMmInitiateAccess = hasAccess;
      });

    this.store
      .select(selectEAuthInfo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((eAuthInfo) => {
        this.eAuthInfo = eAuthInfo;
      });

    this.store
      .select(selectAllEauthAccountHoldersHaveEmailAddress)
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => this.allEauthAccountHoldersHaveAnEmailAddress = val);

    this.store
      .select(selectAcknowledgement)
      .pipe(takeUntil(this.destroy$))
      .subscribe((acknowledgement) => {
        this.acknowledgement = acknowledgement;
      });
    this.errors$ = this.store.select(selectAcknowledgementError);

    this.store.select(selectStandingAuthForm)
    .pipe(takeUntil(this.destroy$),
    withLatestFrom(this.store.select(selectIsFromTableOrView), this.store.select(selectIsIra)))
    .subscribe(([form, isFromTableOrView, isIra]) => {
        this.form = form;
        const checkSaDetails = form?.standingAuthorizationDetails as CheckStandingAuthDetails;
        this.isQcdDistributionReason = checkSaDetails?.distributionReason === distributionReasons.qcd;
        this.isCheckSloa = isCheckStandingAuthDetails(checkSaDetails);
        this.isQCDCheckSloa = isFromTableOrView && isIra && this.isCheckSloa && this.isQcdDistributionReason;

        const wireSaDetails = form?.standingAuthorizationDetails as WireStandingAuthDetails;
        this.isISOCompliant = wireSaDetails?.isISOCompliant ?? false;
      });

    this.formServiceError$ = this.store.select(
      selectStandingAuthFormServiceError
    );

    this.store
      .select(selectWarningList)
      .pipe(takeUntil(this.destroy$))
      .subscribe((warningList) => {
        this.warningList = warningList;
      });

    this.store
      .select(selectAchVerifyCallWarning)
      .pipe(takeUntil(this.destroy$))
      .subscribe(warningMessageList => {
        this.achWarningMessageList = warningMessageList;
      });

    this.store
      .select(selectSaPendReasons)
      .pipe(takeUntil(this.destroy$))
      .subscribe((pendReason) => {
        this.PendingReasonsList = pendReason;
      });

    this.store
      .select(selectIsEmployee)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isEmp) => (this.isEmployee = isEmp));

    this.store
      .select(selectIsDemoUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDemoUser) => (this.isDemoUser = isDemoUser));

    this.reviewPageComponentState$ = this.store.select(selectReviewPage);

    this.submitClicked$ = this.store.select(selectReviewPageSubmitClicked);

    this.store
      .select(selectSLOAVerifyCallFinished)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isVerifyCallFinished) => {
        if(this.isInitiateAndReleaseFlowFromTable && isVerifyCallFinished){
          this.attentionMessageRequired = this.setVerifyMessage();
        }
      });

    if(!this.isInitiateAndReleaseFlowFromTable){
    this.attentionMessageRequired = this.setVerifyMessage();
    }

    this.store
      .select(selectStandingAuthInternalTransferInfo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((info) => {
        this.itInfo = info;
        this.isI401kToAccount = this.itInfo?.toAccountInfo?.registrationType === RegTypeCode.INDIVIDUAL_401K || this.itInfo?.toAccountInfo?.registrationType === RegTypeCode.ROTH_401K;
      });

    this.store
      .select(selectIsSubmitandCreateBundledSa)
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => (this.isSubmitAndCreateBundledSa = val ?? false));

    this.store.select(selectAchAcknowledgeCallError).pipe(takeUntil(this.destroy$)).subscribe(error => {
      this.hasATSSystemError = !!error;
      this.atsSystemErrorText = error;
    })

    this.formServiceError$ = this.store.select(selectStandingAuthFormServiceError);
    this.formServiceError$.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.hasServiceErrorFORINRTable = val.hasError && this.isInitReleaseFlowFromTableVerifyFailure(val.name);
      this.isErrorSubmit = false;

      const hasConflictStatus = val.status === HttpStatusCode.Conflict;
      // Check for "already released" error in both message field and errorList array
      const hasAlreadyReleasedError = val.message === Constants.initReleaseAlreadyReleasedError ||
        val.errorList?.some(err => err.detail === Constants.initReleaseAlreadyReleasedError);

      if (hasConflictStatus || hasAlreadyReleasedError) {
        combineLatest([
          of(this.isInitiateAndReleaseFlowFromTable)
        ]).pipe(
          takeUntil(this.destroy$),
        ).subscribe(([isInitiateAndReleaseFlow]) => {
          if (isInitiateAndReleaseFlow) {
            // Only show toaster for conflict status
            if (hasConflictStatus) {
              this.toastr.error(Constants.initiateAndReleaseDuplicateDraftError, '', {
                toastClass: 'ngx-toastr toast-error tall-toastr',
              });
            }
            // Disable buttons when conflict or already released
            this.shouldButtonsBeDisabled = true;
          }
        });
      }

      if (val?.status === HttpStatusCode.BadRequest) {
        this.hasBadRequestServiceError = true;
        this.scrollToTop();
        if (val.errorList) {
          this.serviceErrorList = val.errorList.map((err) => !!err.detail ? err.detail : err.title);
          if (this.serviceErrorList.includes(Constants.transactionFailedButTweSubmitted)) {
            this.toastr.error(Constants.transactionFailedButTweSubmitted, '', { toastClass: 'ngx-toastr toast-error tall-toastr' });
          }
        }
      }
      else if (val.errorList &&
        (val.errorList.map((err) => err.detail).includes(Constants.atpRequestSloaFailedToggleOnSubmit))) {
        this.hasAtpTimeoutError = true;
        this.isErrorSubmit = true;
        this.scrollToTop();
        this.atpErrorOnSubmitToggleOnBannerTitleText = Constants.atpErrorOnSubmitToggleOnBannerTitleText;
        this.atpErrorOnSubmitToggleOnBannerMessage = Constants.atpErrorOnSubmitToggleOnBannerMessage;
        this.serviceErrorList = [Constants.atpRequestSloaFailedToggleOnSubmit];
      }
      else {
        this.hasServiceError = val.hasError;
        this.scrollToTop();
        this.hasBadRequestServiceError = false;
        this.hasAtpTimeoutError = false;
      }
    });

    this.store.select(selectCheckPendingForCTWE).pipe(takeUntil(this.destroy$)).subscribe((pendingForCTWE => this.checkPendingForCTWE = pendingForCTWE))
    this.store.select(selectInternalTransferPendingForCTWE).pipe(takeUntil(this.destroy$)).subscribe((pendingForCTWE => this.internalTransferPendingForCTWE = pendingForCTWE))

    this.store.select(selectReviewPage).pipe(
      takeUntil(this.destroy$),
      filter(reviewPage => reviewPage.acknowledgeCallFinished),
      withLatestFrom(
        this.store.select(selectAllEauthClientsAwaitingAlliance),
        this.store.select(selectSubAccountId),
        this.store.select(selectStandingAuthPendForRelease),
        this.store.select(selectStandingAuthCanRelease),
        this.store.select(selectHasAccessInitiateMoneyMovement)
      ),
    ).subscribe(([_reviewPage, clientsAwaitingAlliance, subAccountId, pendForRelease, canRelease, hasInitiate]) => {

      let generateLOAINRFlow = (hasInitiate ? !pendForRelease && canRelease : true);

      //If Acknowledge Call finished successfully, call the EmailAddress service for each customer who's still Awaiting Alliance.
      if (!this.eAuthInfo.isOptedOut) {
        clientsAwaitingAlliance.forEach(client => {
          this.store.dispatch(StandingAuthReviewPageActions.clientAwaitingAllianceUsed({ accountId: subAccountId, customerId: client.customerId, emailAddress: client.emailAddress }));
        });
      }

      if (this.transactionType === transactionTypes.check) {
        this.store.dispatch(StandingAuthReviewPageActions.loadConfirmationPage());
        if (((this.clientAuthorizationNeeded && this.eAuthInfo.isOptedOut) || this.checkPendingForCTWE) && generateLOAINRFlow) {
          this.store.dispatch(EAuthActions.generateLoaPdf());
          this.store.dispatch(StandingAuthReviewPageActions.generateAuthorizationForm());
        }
      }
      else if (this.transactionType === transactionTypes.internalTransfer) {
        this.store.dispatch(StandingAuthReviewPageActions.loadConfirmationPage());
        if ((((this.clientAuthorizationNeeded && this.eAuthInfo.isOptedOut) || this.internalTransferPendingForCTWE) && !this.isSubmitAndCreateBundledSa) && generateLOAINRFlow) {
          this.store.dispatch(EAuthActions.generateLoaPdf());
          this.store.dispatch(StandingAuthReviewPageActions.generateAuthorizationForm());
        }
      }
      else if (this.transactionType === transactionTypes.wire) {
        this.store.dispatch(StandingAuthReviewPageActions.loadConfirmationPage());
        if ((this.clientAuthorizationNeeded && this.eAuthInfo.isOptedOut) && generateLOAINRFlow) {
          this.store.dispatch(EAuthActions.generateLoaPdf());
          this.store.dispatch(StandingAuthReviewPageActions.generateAuthorizationForm());
        }
      }
      else if (this.transactionType === transactionTypes.ach) {
        this.store.dispatch(StandingAuthReviewPageActions.loadConfirmationPage());
      }
    });

    this.store.select(selectIsUserInputRequired).subscribe(value => {
      this.userInputRequired = value;
    });

    this.store.select(selectIsOrgAccount).pipe(takeUntil(this.destroy$)).subscribe(isOrgAccount => this.isOrgAccount = isOrgAccount);

    this.store.select(selectInfo).subscribe(accountInfo => {this.isLivingTrustSaFlow = accountInfo?.registrationType?.toLowerCase() === "liv trust";})

    this.store.select(selectPdf).pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data) {
        let buffer = getArrayBufferFromBase64(data);
        let pdf = new Blob([buffer], { type: 'application/pdf' });
        openPdfInNewWindow(pdf, false);
      }
    });

    this.store.select(selectStandingAuthPendForRelease).pipe(takeUntil(this.destroy$)).subscribe(shouldPendForRelease => {
      this.isPendForRelease = shouldPendForRelease;
    });

    this.store.select(selectStandingAuthCanRelease).pipe(takeUntil(this.destroy$)).subscribe(canRelease => {
      this.canRelease = canRelease;
    });

    this.store.select(selectRevalidationResponse).pipe(takeUntil(this.destroy$)).subscribe(status => {
      if (status){
        this.revalidationResponse = status;
      }
    });

    combineLatest([
      this.store.select(selectInitReleaseTransactionInContext),
      this.store.select(selectUsername),
      this.store.select(selectHasAccessReleaseMoneyMovement)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([txnInContext, userName, hasReleaseAccess]) => {
      this.isInitiator = ((txnInContext.createdByLoginId === userName) && userName !== '') || ((txnInContext.createdByLoginId !== '' && !hasReleaseAccess));
      this.isReleasor = hasReleaseAccess;
      this.userName = userName;
    })

    this.store.select(selectInitReleaseTransactionInContext).pipe(takeUntil(this.destroy$)).subscribe(draft => {
      this.currentDraftInContext$.next(mapInitiatedDraftsToTableColumn(draft));
      this.isSloa = draft.draftObject.formType.toLowerCase().includes('sloa');
    })
  }

  onEditClicked() {
    this.store.dispatch(StandingAuthReviewPageActions.editClicked());
    let acknowledgement: Acknowledgement = {
      acknowledgementConsentTime: '',
      isAcknowledgementReviewed: false
    }
    this.store.dispatch(ReviewPageActions.updateAcknowledgement({ acknowledgement }));
  }

  onBackButtonClicked() {
    this.store.dispatch(StandingAuthReviewPageActions.clearAttentionMessage());
    this.store.dispatch(InitReleaseActions.backButtonClicked());
    this.store.dispatch(resetCancelModalforInitiateAndReleaseTable());
    let acknowledgement: Acknowledgement = {
      isAcknowledgementReviewed: false,
      acknowledgementConsentTime: ""
    }
    this.store.dispatch(ReviewPageActions.updateAcknowledgement({ acknowledgement }));
  }

  onSubmit() {
    this.store.select(selectAllSelectedEauthAccountHoldersHaveEmailAddress).pipe(takeUntil(this.destroy$))
      .subscribe(val => this.allEauthAccountHoldersHaveAnEmailAddress = val);
      if (this.isEmployee) {
        this.toastr.error(Constants.toastrMessageEmployee);
        return;
      }
      if (this.isDemoUser) {
        this.toastr.error(Constants.toastrMessageDemoUser);
        return;
      }
      if (!this.userInputRequired && !this.isInitiateAndReleaseFlowFromTable) {
        this.store.dispatch(EAuthActions.optOut())
      } else if (!this.eAuthInfo.isOptedOut && !this.eAuthInfo.isAccountHolderSelected) {
        this.store.dispatch(EAuthActions.invalidSubmitClick());
        return;
      } else if (!this.eAuthInfo.isOptedOut && !this.allEauthAccountHoldersHaveAnEmailAddress) {
        this.store.dispatch(EAuthActions.missingEmailAddressSubmitClick());
        return;
      }

      this.store.dispatch(StandingAuthReviewPageActions.submitClicked());
      if (!this.acknowledgement.isAcknowledgementReviewed) {
        let acknowledgement: Acknowledgement = {
          isAcknowledgementReviewed:
            this.acknowledgement.isAcknowledgementReviewed,
          error: moneyMovementAcknowledgeError,
          acknowledgementConsentTime: '',
        };
        this.store.dispatch(
          ReviewPageActions.updateAcknowledgement({ acknowledgement })
        );
        this.scrollToTop();
      } else {
        this.submitTransaction();
      }
  }

    private submitTransaction() {
        switch (this.form.transactionType) {
            case transactionTypes.ach:
                this.store.dispatch(
                    StandingAuthAchActions.callAcknowledge()
                );
                break;
            case transactionTypes.internalTransfer:
              this.store.dispatch(StandingAuthInternalTransferActions.callAcknowledge());
            break;
            case transactionTypes.check:
                this.store.dispatch(
                    StandingAuthCheckActions.callAcknowledge()
                );
                break;
            case transactionTypes.wire:
                this.store.dispatch(StandingAuthWireActions.callAcknowledgeV3());
                break;
        }
    }

  onCreatePdf(): void {
    this.store.dispatch(StandingAuthReviewPageActions.submitClicked());
    if (!this.acknowledgement.isAcknowledgementReviewed ) {
      this.showAcknowledgementReviewError();
    }
    else{
      this.store.dispatch(StandingAuthReviewPageActions.loadEmptyConfirmationPage());
      this.store.dispatch(EAuthActions.generateLoaPdf());
      this.store.dispatch(StandingAuthReviewPageActions.generateAuthorizationForm());
    }
  }

  showAcknowledgementReviewError(): void {
    let acknowledgement: Acknowledgement = {
      isAcknowledgementReviewed: this.acknowledgement.isAcknowledgementReviewed,
      error: moneyMovementAcknowledgeError,
      acknowledgementConsentTime: ""
    }
    this.store.dispatch(ReviewPageActions.updateAcknowledgement({ acknowledgement }));
    this.scrollToTop();
  }

  setVerifyMessage(): boolean {
    if (this.warningList.length > 0 || this.clientAuthorizationNeeded) {
      this.PendingReasonsList.forEach((pendReason) => {
        if (pendReason) {
          if (pendReason === prettierWarnings.clientAuth ||
            pendReason === prettierWarnings.electronicAuth ||
            pendReason === prettierWarnings.clientAuthTax ||
            pendReason === prettierWarnings.clientAuthNDFALPFA ||
            pendReason === prettierWarnings.clientAuthNonTax) {
            this.warningMessages.add(Constants.clientAuthSignatureForm);
          }
        }
      });

      this.warningList.forEach((warning) => {
        this.warningMessages.add(warning.messageText);
      });
    }

    return this.warningMessages.size > 0;
  }

  isStateTaxAttentionRequiredCT(): boolean {
    if (this.isQcdDistributionReason) {
      return false;
    }
    let isSpecial = ((this.stateTypeIndicator === stateTypeIndicators.Special) && (this.stateCode === stateCodes.CONNECTICUT) && this.reviewStateTax === '0%');
    if (this.defaultStateTax !== "0" && isSpecial) {
      return true;
    }
    return false;
  }

  isStateTaxAttentionRequiredMN(): boolean {
    if (this.isQcdDistributionReason) {
      return false;
    }
    let isSpecial = ((this.stateTypeIndicator === stateTypeIndicators.Special) && (this.stateCode === stateCodes.MINNESOTA) && this.reviewStateTax === '0%');
    if (this.defaultStateTax !== "0" && isSpecial) {
      return true;
    }
    return false;
  }

  isStateTaxAttentionRequiredMI(): boolean {
    if (this.isQcdDistributionReason) {
      return false;
    }
    let isSpecial = ((this.stateTypeIndicator === stateTypeIndicators.Special) && (this.stateCode === stateCodes.MICHIGAN) && this.reviewStateTax === '0%');
    if (this.defaultStateTax !== "0" && isSpecial) {
      return true;
    }
    return false;
  }

  isCharitableContribution(): boolean {
    if (this.itInfo.toAccountInfo.productCode === "CHGFT" && this.itInfo.toAccountInfo.registrationType === "DASCG") {
      return true;
    }
    return false;
  }

  scrollToTop(): void {
    const elem = document.getElementById('sa-review-page-header');
    setTimeout(function () {
      elem?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  downloadPdf(): void {
    this.showPositionsTable = false;
    setTimeout(() => {
      let temp = document.title;
      document.title = 'Move Money - Schwab Advisor Center';
      try {
        positionPage('print-review-page');
        if (!document.execCommand('print', false, '')) {
          window.print();
        }
      } catch {
        positionPage('print-review-page');
        window.print();
      }
      document.title = temp;
    }, 10);
    setTimeout(() => {
      this.showPositionsTable = true
    }, 10);
  }

  get isEditEnabled(): boolean {
    return (!this.isQCDCheckSloa && !this.isInitiateAndReleaseFlowFromTable)
      || this.useTestTrustDigitalToPdfFlow // Flag OFF of new reg type TT
      || this.isI401kToAccount
  }

  get isBackButtonEnabled(): boolean {
    return this.isInitiateAndReleaseFlowFromTable
      && !this.useTestTrustDigitalToPdfFlow  //Default value False
      && !this.isI401kToAccount
  }

  get hasAchWarningMessage(): boolean {
    if (this.achWarningMessageList) {
      return true;
    }
    return false;
  }

  // Add functionality to handle Cancel Transaction on the Review page. Should make correct drafts-api call
  onCancelClicked(): void {
    if (this.isInitiateAndReleaseFlowFromTable && (this.router?.url?.includes('initiate-and-release'))) {
      this.deleteDraftClicked$.next(true);
    } else {
      this.store.dispatch(
        displayCancelModal({
          nextUrlState: navRouteConstants.standingAuth,
          title: 'Cancel this work in progress?',
          content: [
            `The information you have entered to create this standing authorization will be lost.`,
          ],
          greenButton: 'Cancel standing authorization',
          grayButton: 'Back',
          source: 'standingAuthReviewPage'
        })
      );
    }
  }

  isInitReleaseFlowFromTableVerifyFailure(errorName: string){
    return (errorName == ApiEndpointType.initReleaseByDraftId || errorName.toLocaleLowerCase().includes('verify')) && this.isInitiateAndReleaseFlowFromTable;
  }

  get showClientAuthDetailsGoingDigital(): boolean {
    return (
      (this.useTestTrustDigitalToPdfFlow && this.transactionType !== transactionTypes.internalTransfer)
      ||
      (!this.useTestTrustDigitalToPdfFlow && this.transactionType === transactionTypes.internalTransfer)
    );
  }

  get showMoveMoneyAcknowledgementSection(): boolean {
    if (this.transactionType === transactionTypes.internalTransfer) {
      return true;
    }
    return !this.useTestTrustDigitalToPdfFlow;
  }

  get showSubmitButtonForInR() {
    if (this.isInitiateAndReleaseFlowFromTable)  {
      return this.revalidationResponse?.status !== 'invalid' && !this.isInitiator && !this.hasServiceErrorFORINRTable && !this.isNonIsoCompliantSloaReleasorFlow;
    }
    return true;
  }

  get isInRFirm(): boolean {
    return !!this.hasMmInitiateAccess;
  }

  get isInitiateAndReleaseFlowFromTable(): boolean {
    return !!this.hasMmInitiateAccess &&  !!this.currentDraftInContext$.value?.DraftId;
  }

  get submitButtonVerbiage(): string {
    if (this.isInitiateAndReleaseFlowFromTable && this.canRelease) return 'Release for Processing';
    if (this.isPendForRelease) return 'Submit for Review';
    return 'Submit';
  }

  get displayRevalidationError(): boolean {
    if (this.revalidationResponse?.errors?.length == 1) {
      this.singleRevalidationError  = this.revalidationResponse?.errors[0]?.reason ?? '';
      return true;
    }
    else if (this.revalidationResponse?.errors?.length > 1) {
      this.multipleRevalidationError = this.revalidationResponse?.errors.map(fieldInfo => {
        return fieldInfo.reason
      });
      return true;
    }
    return false;
  }

  get cancelButtonVerbiage(): string {
    if (this.isInitiateAndReleaseFlowFromTable && this.isSloa) return 'Delete Standing Authorization';
    return 'Cancel';
  }
  get isNonIsoCompliantSloaReleasorFlow(): boolean {
    return this.transactionType === transactionTypes.wire &&
      this.isInitiateAndReleaseFlowFromTable &&
      !this.isISOCompliant;
  }
}
